import React, { useEffect, useState } from "react";
import api from "@/api";
import { PageTitle, GlassCard, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { Award, Download, Crown, Target, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CoordinatorCertificates() {
  const [certs, setCerts] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/certificates/monthly/mine").then((r) => setCerts(r.data || [])).catch(() => {});
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const download = async (c) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/certificates/monthly/${c.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `شهادة_${c.month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("تعذّر تحميل الشهادة");
    }
  };

  const fmt = (d) => { try { return new Date(d).toLocaleDateString("ar"); } catch { return ""; } };
  const rate = stats?.participation_rate || 0;
  const achieved = rate >= 90;

  return (
    <div className="space-y-6">
      <InspireBanner />
      <PageTitle title="سجل الشهادات" subtitle="أرشيفك الدائم من شهادات الشكر الشهرية ووسام تحقيق المستهدف — جاهزة للطباعة والتصدير" icon={Award} />

      <GlassCard data-testid="my-certificates-card">
        <div className="flex items-center gap-2 mb-4">
          <Crown className="text-amber-500" size={22} />
          <h3 className="font-head font-bold text-lg text-stone-800">شهادات الشكر الشهرية ({certs.length})</h3>
        </div>
        {!certs.length ? (
          <p className="text-stone-500 text-sm py-4 text-center">لم تحصل على شهادة شهرية بعد — استمر في تميّزك وستصلك شهادتك المعتمدة من المدير العام فور استحقاقها 🌟</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {certs.map((c) => (
              <div key={c.id} data-testid={`cert-${c.id}`} className="relative overflow-hidden rounded-2xl p-5 border border-amber-300/60 bg-gradient-to-l from-amber-50 via-white to-sky-50">
                <div className="absolute -left-8 -top-8 w-28 h-28 rounded-full bg-amber-400/15 blur-2xl" />
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-11 h-11 rounded-2xl bg-amber-500/15 flex items-center justify-center shrink-0"><Award className="text-amber-600" size={24} /></div>
                  <div className="min-w-0">
                    <div className="font-head font-bold text-stone-800">شهادة شكر وتقدير</div>
                    <div className="text-[11px] text-sky-700 flex items-center gap-1"><Calendar size={12} /> {c.month_label}</div>
                  </div>
                </div>
                <p className="text-sm text-stone-600 mb-3">معتمدة من المدير العام تقديراً لجهودك المتميّزة في متابعة العمل التطوعي بـ<b className="text-stone-800"> {c.school_name}</b>.</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-stone-400">صدرت: {fmt(c.issued_at)}</span>
                  <button data-testid={`download-cert-${c.id}`} onClick={() => download(c)} className="btn-gold px-4 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
                    <Download size={15} /> تحميل / طباعة PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      <GlassCard data-testid="my-target-badge-card">
        <div className="flex items-center gap-2 mb-4">
          <Target className="text-emerald-600" size={22} />
          <h3 className="font-head font-bold text-lg text-stone-800">وسام تحقيق المستهدف 90%</h3>
        </div>
        {achieved ? (
          <div data-testid="target-badge-earned" className="p-5 rounded-2xl bg-gradient-to-l from-emerald-50 to-amber-50 border border-emerald-300/60 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center shrink-0"><Target className="text-emerald-600" size={28} /></div>
            <div>
              <div className="font-head font-bold text-stone-800">مبارك! حقّقت مدرستك المستهدف 🎯</div>
              <div className="text-sm text-emerald-700 mt-1">نسبة التمكين الحالية {rate}% — تجاوزت مستهدف 90%.</div>
            </div>
          </div>
        ) : (
          <EmptyState text={`نسبة التمكين الحالية ${rate}% — واصل رفع مشاركة الطلبة للوصول إلى المستهدف 90% ونيل الوسام`} icon={Target} />
        )}
      </GlassCard>
    </div>
  );
}
